import { useTranslation } from 'react-i18next';
import { ReportingMetricsCounts } from '../../../../../../../model/ReportingMetrics';
import { useMemo } from 'react';
import { getChartColorByIndex } from '../../../../../../../utils/chartPalette';
import { IMetricsTableRow } from '../MetricsTable/MetricsTable';

export interface IChartItem {
    key: string;
    value: number;
    label: string;
    color?: string;
    trend?: number;
}

export type ChartDataFactory<T> = (sortedItems: readonly IChartItem[]) => T[];

export const useTableChart = <T>(
    counts: ReportingMetricsCounts,
    chartDataFactory: ChartDataFactory<T>
) => {
    const { t } = useTranslation();

    const otherLabel = t('ReportingDashboard_OtherLabel');

    const sortedItems = useMemo(() => {
        return Object.entries(counts)
            .map<IChartItem>(([key, value], i) => {
                return {
                    key: key,
                    label: key || otherLabel,
                    value: value.value,
                    trend: value.trend,
                    color: getChartColorByIndex(i),
                };
            })
            .sort((a, b) => b.value - a.value);
    }, [counts, otherLabel]);

    const tableData = useMemo(() => {
        return sortedItems.map<IMetricsTableRow>((x) => {
            return {
                key: x.key,
                label: x.label,
                value: x.value,
                trend: x.trend,
                legendColor: x.color,
            };
        });
    }, [sortedItems]);

    const chartData = useMemo(
        () => chartDataFactory(sortedItems),
        [sortedItems, chartDataFactory]
    );

    return {
        sortedItems,
        tableData,
        chartData,
    };
};
